import { api, ApiError } from '../api.ts';
import {
  downloadDocument,
  listDocuments,
  openDocument,
  type DocumentMeta,
  type OpenedDocument,
} from '../documents.ts';
import { ablegbareSafes, alleSafes, gaesteNachreichen, ladeSafes, meinSafe, safeVon } from '../safes.ts';
import type { PreparedDocument } from '../aiQuery.ts';
import type { SessionSearchIndex } from '../search.ts';
import type { AufbereitungsErgebnis } from '../aufbereitung/index.ts';
import { clear, el, formatDate, hinweis } from '../ui.ts';

/**
 * Gemeinsamer Unterbau der Ansichten: Sitzungszustand, Kopfzeile mit
 * Navigation und das Nachladen der Dokumente.
 *
 * Zustand lebt nur hier im Arbeitsspeicher. Beim Abmelden wird alles
 * verworfen: entschlüsselte Dokumente, Suchindex und der private Schlüssel
 * (Konzept 3.10 Punkt 4).
 */

export interface Me {
  accountId: string;
  role: 'customer' | 'buddy' | 'admin';
  email: string;
  displayName: string;
  customerId: string | null;
  /** Migration 010/011 — nur bei role 'customer' und nach dem Schlüssel-Upgrade gesetzt. */
  personId: string | null;
  kundeId: string | null;
  orgRolle: string | null;
  /** Gastkonto ohne eigenen Safe (Migration 020). */
  gast?: boolean;
  keySetupDone: boolean;
  /**
   * Gesetzt = der Akten-Safe ruht (Migration 009). Lesen und Herunterladen
   * bleiben erlaubt; Ablegen, Fragen und Freigaben lehnt der Server mit 423
   * ab. Das Feld ist da, damit der Hinweis erscheint, **bevor** jemand
   * vergeblich auf einen Knopf drückt.
   */
  ruht?: { seit: string | null; grund: string | null } | null;
}

export interface Ordner {
  id: string;
  parent_id: string | null;
  name: string;
  created_by: 'ai' | 'customer' | 'buddy';
  /** Nur in der Buddy-Ansicht gesetzt: zu welchem Kunden der Ordner gehört. */
  customer_id?: string;
  /** Zu welchem Safe der Ordner gehört (Familien-Safes); NULL = Altbestand → „Mein Safe". */
  safe_kunde_id?: string | null;
}

export interface State {
  me: Me | null;
  documents: DocumentMeta[];
  opened: OpenedDocument[];
  prepared: PreparedDocument[] | null;
  index: SessionSearchIndex | null;
  ordner: Ordner[];
  /**
   * Nur für Buddys: Auf welchen Kunden die Ansicht gerade eingegrenzt ist
   * (`customer_id`). Ein Buddy kann Freigaben mehrerer Kunden gleichzeitig
   * haben; ohne diese Eingrenzung lägen alle Unterlagen in einem Topf.
   *
   * `null` heißt „noch nicht gewählt" und kommt nur vor, solange gar keine
   * Freigabe vorliegt — `buddyKundeSetzen` wählt sonst immer einen aus.
   */
  buddyKunde: string | null;
  /**
   * Nur für Kunden (Familien-Safes, 16.10.2026): Auf welchen Safe Suche, Frage und
   * Dokumentliste gerade eingegrenzt sind. `null` = überall, wo ich darf – die
   * Vorgabe. Ein Safe ist nur noch ein Filter, kein Umschalten mehr.
   */
  suchbereich: string | null;
  /** Nur für Kunden: In welchen Safe neue Dokumente gelegt werden (zuletzt gewählt). */
  ablageziel: string | null;
}

const MERKER_SUCHBEREICH = 'kf-safe-suchbereich';
const MERKER_ABLAGEZIEL = 'kf-safe-ablageziel';

function gemerkt(schluessel: string): string | null {
  try {
    return window.localStorage.getItem(schluessel);
  } catch {
    return null;
  }
}

export const state: State = {
  me: null,
  documents: [],
  opened: [],
  prepared: null,
  index: null,
  ordner: [],
  buddyKunde: null,
  suchbereich: gemerkt(MERKER_SUCHBEREICH),
  ablageziel: gemerkt(MERKER_ABLAGEZIEL),
};

// ---------------------------------------------------------------------------
// Kundeneingrenzung der Buddy-Ansicht
// ---------------------------------------------------------------------------

/**
 * Die Dokumente, die gerade gezeigt, durchsucht und befragt werden dürfen.
 *
 * Für Kunden sind das schlicht alle eigenen. Für einen Buddy ausschließlich
 * die des gewählten Kunden — **jede** Stelle, die Dokumente anfasst, muss
 * hierüber gehen und nicht direkt über `state.opened`. Sonst durchsucht eine
 * Suche die Unterlagen eines Kunden mit, um den es gerade nicht geht.
 */
export function sichtbareDokumente(): OpenedDocument[] {
  if (state.me?.role === 'customer') {
    if (!state.suchbereich) return state.opened;
    return state.opened.filter((dokument) => safeIdVon(dokument.meta.safe_kunde_id) === state.suchbereich);
  }
  if (state.me?.role !== 'buddy' || !state.buddyKunde) return state.opened;
  return state.opened.filter((dokument) => dokument.meta.customer_id === state.buddyKunde);
}

/**
 * Zu welchem Safe etwas gehört – Altbestand ohne Safe-Kennung zählt zu „Mein Safe".
 * Gibt es keinen eigenen Safe (Konto von vor dem Organisationsmodell), bleibt es null.
 */
export function safeIdVon(kundeId: string | null | undefined): string | null {
  return kundeId ?? meinSafe()?.kundeId ?? null;
}

/**
 * Suchbereich wählen (Kacheln „Wo suchen?"). Suchindex und KI-Vorbereitung werden
 * dabei verworfen – aus demselben Grund wie beim Kundenwechsel des Buddys: Beide
 * sind aus der Dokumentmenge gebaut, und die ändert sich gerade.
 */
export function suchbereichSetzen(kundeId: string | null): void {
  if (state.suchbereich === kundeId) return;
  state.suchbereich = kundeId;
  state.index = null;
  state.prepared = null;
  try {
    if (kundeId) window.localStorage.setItem(MERKER_SUCHBEREICH, kundeId);
    else window.localStorage.removeItem(MERKER_SUCHBEREICH);
  } catch {
    // Merken ist Komfort, kein Muss.
  }
}

export function ablagezielSetzen(kundeId: string | null): void {
  state.ablageziel = kundeId;
  try {
    if (kundeId) window.localStorage.setItem(MERKER_ABLAGEZIEL, kundeId);
    else window.localStorage.removeItem(MERKER_ABLAGEZIEL);
  } catch {
    // s. o.
  }
}

/** Der Safe, in den gerade abgelegt wird – gemerkt, sonst „Mein Safe", sonst der erste erlaubte. */
export function aktuellesAblageziel(): string | null {
  const erlaubt = ablegbareSafes();
  if (state.ablageziel && erlaubt.some((s) => s.kundeId === state.ablageziel)) return state.ablageziel;
  return meinSafe()?.kundeId ?? erlaubt[0]?.kundeId ?? null;
}

/**
 * Die Ordner, die zur gerade gewählten Freigabe gehören.
 *
 * Der Server liefert dem Buddy die Ordner **aller** Kunden mit laufender
 * Freigabe; ohne diese Eingrenzung stünden im Baum fremde Ordnernamen — und
 * Ordnernamen sind unverschlüsselt, sie verraten also durchaus etwas.
 */
export function sichtbareOrdner(): Ordner[] {
  if (state.me?.role === 'customer') {
    if (!state.suchbereich) return state.ordner;
    return state.ordner.filter((ordner) => safeIdVon(ordner.safe_kunde_id) === state.suchbereich);
  }
  if (state.me?.role !== 'buddy' || !state.buddyKunde) return state.ordner;
  return state.ordner.filter((ordner) => ordner.customer_id === state.buddyKunde);
}

/** Ordner eines bestimmten Safes (für „Wohin legen?" und „Verschieben"). */
export function ordnerDesSafes(kundeId: string | null): Ordner[] {
  return state.ordner.filter((ordner) => safeIdVon(ordner.safe_kunde_id) === safeIdVon(kundeId));
}

/** Ein Ordner samt aller Unterordner – als Menge von Kennungen. */
export function ordnerMitUnterordnern(folderIds: string[]): Set<string> {
  const menge = new Set(folderIds);
  let gewachsen = true;
  while (gewachsen) {
    gewachsen = false;
    for (const o of state.ordner) {
      if (o.parent_id && menge.has(o.parent_id) && !menge.has(o.id)) {
        menge.add(o.id);
        gewachsen = true;
      }
    }
  }
  return menge;
}

/** Kunden, die dem Buddy gerade Zugriff erteilt haben – nach Namen sortiert. */
export function buddyKunden(): Array<{ id: string; name: string }> {
  const gefunden = new Map<string, string>();
  for (const dokument of state.opened) {
    const id = dokument.meta.customer_id;
    if (id && !gefunden.has(id)) gefunden.set(id, dokument.meta.kunde_name ?? 'Unbekannt');
  }
  return [...gefunden]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name, 'de'));
}

/**
 * Wechselt den Kunden und wirft dabei Suchindex und KI-Vorbereitung weg.
 *
 * Das Wegwerfen ist kein Aufräumen, sondern der Kern der Sache: Beide werden
 * einmal aus der Dokumentmenge gebaut und danach wiederverwendet. Blieben sie
 * beim Wechsel stehen, würde eine Suche weiter die Unterlagen des vorigen
 * Kunden durchsuchen und die KI-Frage sie an das Modell schicken — ein
 * Datenabfluss quer über zwei Freigaben hinweg.
 */
export function buddyKundeSetzen(customerId: string | null): void {
  if (state.buddyKunde === customerId) return;
  state.buddyKunde = customerId;
  state.index = null;
  state.prepared = null;
}

export const root = document.querySelector<HTMLElement>('#app')!;

export function fehlertext(error: unknown): string {
  if (error instanceof ApiError || error instanceof Error) return error.message;
  return 'Unbekannter Fehler';
}

// ---------------------------------------------------------------------------
// Ticketbezug (Konzept 6.6)
// ---------------------------------------------------------------------------

/**
 * Ticketbezug aus dem Link der Einladungsmail (Konzept 6.6.2 in main.ts).
 * Einmal beim Start gelesen; die Adresse bereinigt main.ts danach.
 *
 * Der Wert ist ausdrücklich **kein** Nachweis: Er kommt aus der Adresszeile
 * und ist damit frei wählbar. Er beschriftet die Freigabe, er berechtigt zu
 * nichts.
 */
function ticketBezug(): { nummer: string; betreff: string | null } | null {
  const parameter = new URLSearchParams(window.location.search);
  const nummer = parameter.get('ticket')?.trim();
  if (!nummer || !/^T-\d{3,9}$/.test(nummer)) return null;
  const betreff = parameter.get('anliegen')?.trim().slice(0, 200) || null;
  return { nummer, betreff };
}

export const ticket = ticketBezug();

// ---------------------------------------------------------------------------
// Kopfzeile mit Navigation
// ---------------------------------------------------------------------------

export type AnsichtId = 'start' | 'dokumente' | 'freigaben' | 'konto' | 'organisation';

/**
 * Der Reiter „Organisation" (Kreise, Einladungen) erscheint nur, wenn das
 * Konto überhaupt einen Organisationsbezug hat (`personId` gesetzt,
 * Migration 010) — für die meisten Bestandskunden ohne Schlüssel-Upgrade
 * bliebe er sonst ein Reiter ohne Inhalt.
 */
function tabsFuerKunde(): Array<{ id: AnsichtId; hash: string; text: string }> {
  const tabs: Array<{ id: AnsichtId; hash: string; text: string }> = [
    { id: 'start', hash: '#/', text: 'Startseite' },
    { id: 'dokumente', hash: '#/dokumente', text: 'Dokumente' },
    { id: 'freigaben', hash: '#/freigaben', text: 'Freigaben' },
  ];
  // Kein Reiter „Organisation" mehr (17.10.2026): Vereine und Firmen werden unter
  // „Freigaben" genauso bedient wie die Familie – Mitglieder, Fächer, Aufnehmen.
  tabs.push({ id: 'konto', hash: '#/konto', text: 'Mein Konto' });
  return tabs;
}

const TABS_BUDDY: Array<{ id: AnsichtId; hash: string; text: string }> = [
  { id: 'dokumente', hash: '#/dokumente', text: 'Dokumente' },
];

let abmeldenHandler: () => void = () => {};

/** main.ts hängt hier das Abmelden ein – so entsteht kein Import-Kreis. */
export function registriereAbmelden(fn: () => void): void {
  abmeldenHandler = fn;
}

/**
 * Nach einem Organisationswechsel muss `state.me` neu geladen (andere
 * `kundeId`/`orgRolle`) und die aktuelle Ansicht neu gerendert werden — beides
 * lebt in main.ts, das umgekehrt diese Datei importiert. Deshalb dieselbe
 * Umgehung wie beim Abmelden: main.ts hängt sich hier ein.
 */
let organisationsWechselHandler: () => Promise<void> = async () => {};

export function registriereOrganisationsWechsel(fn: () => Promise<void>): void {
  organisationsWechselHandler = fn;
}

/** Nach einem (jetzt seltenen) Organisationswechsel – z. B. durch die Organisationsansicht. */
export function organisationsWechselAusloesen(): Promise<void> {
  return organisationsWechselHandler();
}

export const ROLLENNAME: Record<string, string> = {
  inhaber: 'Inhaber',
  mitglied: 'Mitglied',
  vertrauensperson: 'Vertrauensperson',
};

/**
 * Kopfzeile mit Wortmarke und – für Kunden und Buddys – dem Hauptmenü.
 * Die Reiter sind echte Verweise auf Routen (`#/…`): vor- und zurück im
 * Browser funktionieren damit von selbst, main.ts rendert bei `hashchange`.
 */
export function kopfzeile(aktiv?: AnsichtId): HTMLElement {
  const tabs =
    state.me?.role === 'customer'
      ? tabsFuerKunde()
      : state.me?.role === 'buddy'
        ? TABS_BUDDY
        : [];

  // Der frühere Organisations-Umschalter ist weg (16.10.2026): Alle Safes sind
  // gleichzeitig offen, gewählt wird über Kacheln in den Ansichten.
  return el(
    'header',
    { class: 'kopf' },
    el(
      'div',
      { class: 'kopf-marke' },
      el('h1', {}, el('span', { class: 'tb-wordmark' }, 'KF'), ' Akten-Safe'),
      state.me
        ? el(
            'p',
            { class: 'leise' },
            `Angemeldet als ${state.me.displayName}` +
              (state.me.role === 'buddy' ? ' (Mitarbeiter)' : ''),
          )
        : null,
    ),
    el(
      'div',
      { class: 'kopf-aktionen' },
      el(
        'button',
        { class: 'sekundaer', onclick: () => { window.location.hash = '#/hilfe'; } },
        'Hilfe & FAQ',
      ),
      el('button', { class: 'sekundaer', onclick: () => abmeldenHandler() }, 'Abmelden'),
    ),
    tabs.length > 0
      ? el(
          'nav',
          { class: 'hauptnav', 'aria-label': 'Hauptmenü' },
          ...tabs.map((tab) =>
            el(
              'a',
              {
                class: tab.id === aktiv ? 'nav-reiter aktiv' : 'nav-reiter',
                href: tab.hash,
                ...(tab.id === aktiv ? { 'aria-current': 'page' } : {}),
              },
              tab.text,
            ),
          ),
        )
      : null,
    // Ruhender Zugang: steht in der Kopfzeile und damit auf jeder Ansicht.
    // Ein Hinweis, den man nur auf der Startseite sieht, wird von dem
    // übersehen, der direkt auf „Dokumente" springt.
    state.me?.ruht
      ? el(
          'p',
          { class: 'ruht-streifen', role: 'status' },
          el('strong', {}, 'Ihr Akten-Safe ruht. '),
          'Ihre Unterlagen bleiben vollständig erhalten — Ansehen und Herunterladen ' +
            'sind weiterhin möglich. Neues Ablegen, Fragen an die Ablage und Freigaben ' +
            'sind währenddessen nicht möglich.' +
            (state.me.ruht.grund ? ` Grund: ${state.me.ruht.grund}` : ''),
        )
      : null,
  );
}

export function buddyHinweis(): HTMLElement {
  return hinweis(
    'Sie sehen hier ausschließlich Dokumente, für die eine Kundin oder ein Kunde ' +
      'Ihnen gerade Zugriff erteilt hat. Nach Ablauf der Freigabe verschwinden sie.',
  );
}

// ---------------------------------------------------------------------------
// Daten laden
//
// Zwei Stufen, damit jede Ansicht nur bezahlt, was sie braucht: Die
// Metadaten (Liste + Ordner) sind ein schneller Abruf. Das Entschlüsseln
// aller Dokumente dagegen kostet spürbar Zeit – Startseite, Freigaben und
// Mein Konto kommen ohne aus.
// ---------------------------------------------------------------------------

let metadatenLauf: Promise<void> | null = null;
let entschluesseltLauf: Promise<void> | null = null;

export function sorgeFuerMetadaten(): Promise<void> {
  metadatenLauf ??= (async () => {
    const kunde = state.me?.role === 'customer';
    // Erst die Safes (schließt sie auf), dann Dokumente und Ordner aller Safes.
    if (kunde && state.me?.personId) await ladeSafes();
    const [dokumente, ordnerAntwort] = await Promise.all([
      listDocuments(kunde),
      api.get<{ folders: Ordner[] }>(kunde ? '/api/safes/ordner' : '/api/folders'),
    ]);
    state.documents = dokumente;
    state.ordner = ordnerAntwort.folders;
    // Ein gemerkter Suchbereich, den es nicht mehr gibt (Schlüssel zurückgezogen),
    // fällt still auf „Überall" zurück.
    if (kunde && state.suchbereich && !safeVon(state.suchbereich)) suchbereichSetzen(null);
  })().catch((error: unknown) => {
    metadatenLauf = null; // Ein Fehlschlag darf nicht als Ergebnis hängenbleiben.
    throw error;
  });
  return metadatenLauf;
}

export function sorgeFuerEntschluesselte(): Promise<void> {
  entschluesseltLauf ??= (async () => {
    await sorgeFuerMetadaten();
    const geoeffnet: OpenedDocument[] = [];
    for (const meta of state.documents) {
      try {
        geoeffnet.push(await openDocument(meta));
      } catch {
        // Ein einzelnes nicht entschlüsselbares Dokument darf die ganze
        // Liste nicht blockieren – es wird in der Ansicht gesondert gezählt.
      }
    }
    state.opened = geoeffnet;
    // Gäste, die inzwischen ihren Schlüssel haben, bekommen jetzt still ihre Umschläge.
    if (state.me?.role === 'customer' && state.me.personId) {
      void gaesteNachreichen(
        geoeffnet.map((d) => ({ id: d.id, dek: d.dek, folderId: d.meta.folder_id, safeKundeId: d.meta.safe_kunde_id ?? null })),
        ordnerMitUnterordnern,
      ).catch(() => undefined);
    }
  })().catch((error: unknown) => {
    entschluesseltLauf = null;
    throw error;
  });
  return entschluesseltLauf;
}

/** Nach Upload oder Löschen: alles neu laden (auch neu entschlüsseln). */
export function datenVerwerfen(): void {
  metadatenLauf = null;
  entschluesseltLauf = null;
  state.documents = [];
  state.opened = [];
  state.ordner = [];
  state.index?.clear();
  state.index = null;
  state.prepared = null;
}

/**
 * Nach Ordner-Änderungen genügt es, die Metadaten neu zu holen – die bereits
 * entschlüsselten Dokumente bleiben gültig und müssen nicht noch einmal
 * durch die Kryptographie.
 */
export async function metadatenAuffrischen(): Promise<void> {
  metadatenLauf = null;
  await sorgeFuerMetadaten();
  const nachId = new Map(state.documents.map((meta) => [meta.id, meta]));
  state.opened = state.opened
    .filter((dokument) => nachId.has(dokument.id))
    .map((dokument) => ({ ...dokument, meta: nachId.get(dokument.id)! }));
}

// ---------------------------------------------------------------------------
// Dokument öffnen
// ---------------------------------------------------------------------------

/** Blob-URLs geöffneter Dokumente – beim Abmelden zu widerrufen. */
const geoeffneteUrls: string[] = [];

export function geoeffneteUrlsWiderrufen(): void {
  for (const url of geoeffneteUrls.splice(0)) URL.revokeObjectURL(url);
}

export async function oeffnen(documentId: string): Promise<void> {
  const dokument = state.opened.find((entry) => entry.id === documentId);
  if (!dokument) return;

  // Das Tab MUSS noch im Klick-Handler entstehen, bevor der erste `await`
  // läuft – sonst gilt es als nicht mehr nutzergesteuert und Safari (vor
  // allem am iPad) blockiert `window.open` danach lautlos: kein Fehler,
  // kein neues Tab, nichts passiert.
  //
  // Bewusst OHNE das Merkmal 'noopener': Damit gäbe window.open laut
  // Spezifikation IMMER null zurück, und das Tab ließe sich nie mit der
  // Datei befüllen. Der Rückweg wird stattdessen von Hand gekappt.
  const fenster = window.open('', '_blank');
  if (!fenster) {
    window.alert(
      'Ihr Browser hat das Fenster blockiert. Bitte erlauben Sie Pop-ups für ' +
        'diese Seite und klicken Sie erneut auf „Öffnen".',
    );
    return;
  }
  fenster.opener = null;
  try {
    fenster.document.write(
      '<p style="font-family:system-ui;padding:2rem">Dokument wird entschlüsselt …</p>',
    );
  } catch {
    // Nur eine Zwischenanzeige – wenn sie nicht geht, geht der Rest trotzdem.
  }

  try {
    const blob = await downloadDocument(dokument);
    const url = URL.createObjectURL(blob);
    fenster.location.replace(url);
    // NICHT nach kurzer Frist widerrufen: iPadOS verwirft Hintergrund-Tabs
    // unter Speicherdruck und lädt sie bei Rückkehr neu – eine widerrufene
    // blob-URL ergäbe dann „Safari kann die Seite nicht öffnen". Die URLs
    // sterben spätestens mit dieser Seite; vorher räumt das Abmelden auf.
    geoeffneteUrls.push(url);
  } catch (error) {
    fenster.close();
    window.alert(fehlertext(error));
  }
}

// ---------------------------------------------------------------------------
// Aufbereitung (Foto → PDF, Texterkennung)
// ---------------------------------------------------------------------------

/**
 * Die Aufbereitung wird erst geladen, wenn wirklich etwas abgelegt wird.
 *
 * Sie bringt Texterkennung, PDF-Erzeugung und Bildverarbeitung mit und ist
 * damit größer als die ganze übrige Anwendung. Wer sich nur anmeldet, um ein
 * Dokument nachzuschlagen, soll darauf nicht warten – gerade nicht auf einer
 * Mobilfunkverbindung.
 */
let aufbereitungModul: typeof import('../aufbereitung/index.ts') | null = null;

export async function ladeAufbereitung(): Promise<typeof import('../aufbereitung/index.ts')> {
  aufbereitungModul ??= await import('../aufbereitung/index.ts');
  return aufbereitungModul;
}

/** Beim Abmelden: den Arbeiter der Texterkennung aufräumen, falls geladen. */
export function beendeAufbereitung(): void {
  void aufbereitungModul?.beendeTexterkennung();
}

/**
 * Zeigt einen Arbeitsschritt an, bei Bedarf mit Fortschrittsbalken.
 *
 * Die Aufbereitung eines Fotos dauert je nach Gerät zehn bis dreißig
 * Sekunden. Ohne sichtbaren Fortschritt hält ein Teil der Nutzer das für
 * einen Absturz und lädt die Seite neu – womit die Arbeit von vorn beginnt.
 */
export function zeigeSchritt(status: HTMLElement, schritt: string, anteil?: number): void {
  clear(status);
  status.append(el('p', { class: 'leise' }, `${schritt} …`));
  if (anteil !== undefined && anteil > 0 && anteil < 1) {
    const balken = el('div', { class: 'fortschritt' });
    balken.append(el('div', { style: `width:${Math.round(anteil * 100)}%` }));
    status.append(balken);
  }
}

/**
 * Bereitet die gewählte Datei auf und fragt beim Zuschnitt zurück.
 *
 * Der Rückfrageweg ist der Kern der Zusage „kein Dokument erkannt": Statt
 * irgendein Bild abzulegen, sagt die Anwendung, was sie nicht gefunden hat,
 * und bietet den Zuschnitt von Hand an. Wer abbricht, bekommt nichts abgelegt.
 */
export async function aufbereiten(
  datei: File,
  bytes: Uint8Array,
  status: HTMLElement,
): Promise<AufbereitungsErgebnis | null> {
  zeigeSchritt(status, 'Aufbereitung wird vorbereitet');
  const modul = await ladeAufbereitung();

  const aufbereitung = await modul.bereiteVor(datei, bytes, {
    melde: (schritt: string, anteil?: number) => zeigeSchritt(status, schritt, anteil),
    frage: async (text: string) => window.confirm(text),
  });
  if (aufbereitung.art === 'fertig') return aufbereitung.ergebnis;

  if (aufbereitung.art === 'bestaetigen') {
    // Automatisch erkannt, aber vorgelegt statt sofort verwendet: Auf einem
    // hellen, schwach gemusterten Untergrund (Marmor, Naturstein) kann der
    // gefundene Ausschnitt einen Rand einschließen, der sich weder an der
    // Fläche noch am Winkel als falsch zu erkennen gibt (Konzept-Ergänzung
    // 11.08.2026). Ein Tipp auf „Übernehmen“ genügt, wenn er schon passt.
    clear(status);
    const zuschnitt = await modul.zuschneidenLassen(
      aufbereitung.bild,
      aufbereitung.vorschlag,
      'Ausschnitt bestätigen',
      'So wurde Ihr Dokument erkannt. Passt der Ausschnitt, klicken Sie direkt auf ' +
        '„Ausschnitt übernehmen". Andernfalls ziehen Sie die vier Ecken auf die ' +
        'Ecken Ihres Dokuments.',
    );
    if (!zuschnitt) return null;
    return aufbereitung.weiter(zuschnitt.ecken, zuschnitt.drehung);
  }

  clear(status);
  const entscheidung = await new Promise<boolean>((antwort) => {
    status.append(
      hinweis(
        `Auf „${datei.name}" ist kein Dokument zu erkennen. ${aufbereitung.grund} ` +
          'Wenn dort doch eines abgebildet ist, können Sie den Ausschnitt selbst ' +
          'festlegen.',
        'warnung',
      ),
      el(
        'div',
        { class: 'knopfreihe' },
        el('button', { type: 'button', onclick: () => antwort(true) }, 'Ausschnitt selbst festlegen'),
        el(
          'button',
          { type: 'button', class: 'sekundaer', onclick: () => antwort(false) },
          'Abbrechen',
        ),
      ),
    );
  });

  if (!entscheidung) return null;

  const zuschnitt = await modul.zuschneidenLassen(
    aufbereitung.bild,
    aufbereitung.vorschlag,
    'Ausschnitt festlegen',
    'Ziehen Sie die vier Ecken auf die Ecken Ihres Dokuments. Der graue Bereich ' +
      'wird abgeschnitten.',
  );
  if (!zuschnitt) return null;

  return aufbereitung.weiter(zuschnitt.ecken, zuschnitt.drehung);
}

// ---------------------------------------------------------------------------
// Warteschlange – mehrere Dateien nacheinander ablegen
// ---------------------------------------------------------------------------

export type StapelErgebnis =
  | { art: 'erfolg'; text: string; hinweise?: string[] }
  | { art: 'uebersprungen'; text: string }
  | { art: 'fehler'; text: string };

export interface StapelBericht {
  erfolge: number;
  uebersprungen: number;
  fehler: number;
  abgebrochen: boolean;
  /** Die tatsächlich abgelegten Dateien – alles andere bleibt zur Auswahl. */
  erledigt: File[];
}

type StapelZustand = 'wartet' | 'laeuft' | 'erfolg' | 'uebersprungen' | 'fehler';

const ZUSTAND_TEXT: Record<StapelZustand, string> = {
  wartet: 'Wartet',
  laeuft: 'In Arbeit',
  erfolg: 'Abgelegt',
  uebersprungen: 'Übersprungen',
  fehler: 'Fehlgeschlagen',
};

/**
 * Arbeitet eine Auswahl von Dateien der Reihe nach ab.
 *
 * Bewusst eine nach der anderen statt alle auf einmal: Die Aufbereitung
 * eines Fotos belegt den Rechner für zehn bis dreißig Sekunden vollständig
 * (Zuschnitt, Entzerrung, Texterkennung), und sie fragt bei unklarem
 * Ausschnitt zurück. Parallel liefe der Browser fest und mehrere
 * Zuschnitt-Dialoge kämen gleichzeitig — nacheinander bleibt die Anwendung
 * bedienbar und jede Rückfrage steht für sich.
 *
 * Ein Fehler bei einer Datei beendet den Lauf nicht: Er wird an der Datei
 * vermerkt, die übrigen laufen weiter. Wer 20 Unterlagen ablegt, will nicht
 * bei Nummer 3 von vorn anfangen.
 */
export async function stapelVerarbeiten(
  dateien: File[],
  behaelter: HTMLElement,
  verarbeite: (datei: File, status: HTMLElement) => Promise<StapelErgebnis>,
): Promise<StapelBericht> {
  clear(behaelter);

  const mehrere = dateien.length > 1;
  let abbrechen = false;

  const fortschritt = el('strong', {}, '');
  const abbruch = el(
    'button',
    {
      type: 'button',
      class: 'sekundaer schmal',
      onclick: () => {
        abbrechen = true;
        abbruch.disabled = true;
        abbruch.textContent = 'Wird angehalten …';
      },
    },
    'Rest abbrechen',
  ) as HTMLButtonElement;

  const liste = el('ol', { class: 'warteschlange' });
  if (mehrere) behaelter.append(el('div', { class: 'ws-leiste' }, fortschritt, abbruch));
  behaelter.append(liste);

  // Alle Einträge vorab anlegen: So ist von Anfang an sichtbar, was noch
  // kommt – und wie lange es voraussichtlich noch dauert.
  const eintraege = dateien.map((datei) => {
    const zustand = el('span', { class: 'ws-zustand' }, ZUSTAND_TEXT.wartet);
    const status = el('div', { class: 'ws-status' });
    const zeile = el(
      'li',
      { class: 'ws-eintrag', 'data-zustand': 'wartet' },
      el(
        'div',
        { class: 'ws-kopf' },
        el('span', { class: 'ws-punkt', 'aria-hidden': 'true' }),
        el('span', { class: 'ws-name' }, datei.name),
        zustand,
      ),
      status,
    );
    liste.append(zeile);
    return { datei, zeile, zustand, status };
  });

  const setze = (
    eintrag: (typeof eintraege)[number],
    art: StapelZustand,
    text?: string,
  ): void => {
    eintrag.zeile.setAttribute('data-zustand', art);
    eintrag.zustand.textContent = ZUSTAND_TEXT[art];
    if (text !== undefined) {
      clear(eintrag.status);
      if (text) eintrag.status.append(el('p', { class: 'leise' }, text));
    }
  };

  const bericht: StapelBericht = {
    erfolge: 0,
    uebersprungen: 0,
    fehler: 0,
    abgebrochen: false,
    erledigt: [],
  };

  for (const [index, eintrag] of eintraege.entries()) {
    if (abbrechen) {
      setze(eintrag, 'uebersprungen', 'Abgebrochen – nicht abgelegt.');
      bericht.uebersprungen += 1;
      bericht.abgebrochen = true;
      continue;
    }

    fortschritt.textContent = `Datei ${index + 1} von ${dateien.length}`;
    setze(eintrag, 'laeuft');
    eintrag.zeile.scrollIntoView({ block: 'nearest' });

    try {
      const ergebnis = await verarbeite(eintrag.datei, eintrag.status);
      setze(eintrag, ergebnis.art, ergebnis.text);
      if (ergebnis.art === 'erfolg') {
        bericht.erfolge += 1;
        bericht.erledigt.push(eintrag.datei);
        for (const text of ergebnis.hinweise ?? []) {
          eintrag.status.append(hinweis(text, 'warnung'));
        }
      } else if (ergebnis.art === 'uebersprungen') {
        bericht.uebersprungen += 1;
      } else {
        bericht.fehler += 1;
      }
    } catch (error) {
      setze(eintrag, 'fehler', fehlertext(error));
      bericht.fehler += 1;
    }
  }

  if (mehrere) {
    fortschritt.textContent = bericht.abgebrochen
      ? 'Angehalten'
      : `${dateien.length} von ${dateien.length} bearbeitet`;
    abbruch.remove();
  }

  return bericht;
}

/** Ergebnis eines Stapellaufs in einem Satz – für die Meldung danach. */
export function stapelMeldung(bericht: StapelBericht): HTMLElement {
  const teile: string[] = [];
  if (bericht.erfolge > 0) {
    teile.push(
      bericht.erfolge === 1 ? '1 Dokument abgelegt' : `${bericht.erfolge} Dokumente abgelegt`,
    );
  }
  if (bericht.uebersprungen > 0) teile.push(`${bericht.uebersprungen} übersprungen`);
  if (bericht.fehler > 0) teile.push(`${bericht.fehler} fehlgeschlagen`);

  const satz = teile.length > 0 ? `${teile.join(', ')}.` : 'Es wurde nichts abgelegt.';
  const art = bericht.fehler > 0 ? 'warnung' : bericht.erfolge > 0 ? 'erfolg' : 'warnung';
  return hinweis(bericht.abgebrochen ? `${satz} Der Rest wurde abgebrochen.` : satz, art);
}

// ---------------------------------------------------------------------------
// Zugriffsprotokoll – gemeinsame Bausteine für Feed und Freigaben-Ansicht
// ---------------------------------------------------------------------------

export interface LogRow {
  id: string;
  action: string;
  accessed_at: string;
  actor_name: string | null;
  actor_role: string;
  document_id?: string | null;
  detail: { documentCount?: number } | null;
}

export const AKTION_TEXT: Record<string, string> = {
  grant_created: 'Freigabe erteilt',
  grant_revoked: 'Freigabe zurückgezogen',
  grant_expired: 'Freigabe abgelaufen',
  view: 'Dokument angesehen',
  download: 'Dokument geöffnet',
  upload: 'Dokument abgelegt',
  delete: 'Dokument gelöscht',
  ai_query: 'Frage an die KI gestellt',
  gast_freigabe: 'Schlüssel zum Safe gegeben',
  gast_entzogen: 'Schlüssel zum Safe zurückgezogen',
  dienst_freigabe: 'Ordner für einen Dienst freigegeben',
  dienst_lesen: 'Dienst hat ein Dokument gelesen',
  dienst_ablage: 'Dienst hat ein Dokument abgelegt',
};

export function protokollAkteur(entry: LogRow): string {
  return entry.actor_role === 'customer' ? 'Sie selbst' : (entry.actor_name ?? 'Unbekannt');
}

export function protokollTabelle(entries: LogRow[]): HTMLElement {
  return el(
    'table',
    {},
    el(
      'thead',
      {},
      el('tr', {}, el('th', {}, 'Wann'), el('th', {}, 'Wer'), el('th', {}, 'Was')),
    ),
    el(
      'tbody',
      {},
      ...entries.map((entry) =>
        el(
          'tr',
          {},
          el('td', {}, formatDate(entry.accessed_at)),
          el('td', {}, protokollAkteur(entry)),
          el(
            'td',
            {},
            AKTION_TEXT[entry.action] ?? entry.action,
            entry.detail?.documentCount ? ` (${entry.detail.documentCount} Dokumente)` : '',
          ),
        ),
      ),
    ),
  );
}

// ---------------------------------------------------------------------------
// Herunterladen und Voll-Export
// ---------------------------------------------------------------------------

function speichern(blob: Blob, dateiname: string): void {
  const url = URL.createObjectURL(blob);
  const verweis = el('a', { href: url, download: dateiname });
  document.body.append(verweis);
  verweis.click();
  verweis.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Dateiname sicher für Dateisystem und ZIP (Pfadtrenner und Steuerzeichen raus). */
function sauber(name: string): string {
  return name.replace(/[\\/:*?"<>|\u0000-\u001f]/g, '_').replace(/^\.+/, '_').trim() || 'Unbenannt';
}

/** Lädt ein Dokument unter seinem richtigen Namen herunter (nicht „Unknown"). */
export async function herunterladen(documentId: string): Promise<void> {
  const dokument = state.opened.find((entry) => entry.id === documentId);
  if (!dokument) return;
  try {
    speichern(await downloadDocument(dokument), sauber(dokument.filename));
  } catch (error) {
    window.alert(fehlertext(error));
  }
}

/**
 * Voll-Export: alle Dokumente mit der Ordnerstruktur als ZIP. Entschlüsselt wird auf dem Gerät
 * (Zero-Knowledge). Bei vielen Dateien entstehen mehrere Pakete von je etwa `paketBytes`.
 */
export async function vollExport(
  fortschritt: (text: string) => void,
  paketBytes = 250 * 1024 * 1024,
): Promise<{ dokumente: number; pakete: number; fehler: string[] }> {
  await sorgeFuerEntschluesselte();
  const ordnerNachId = new Map(state.ordner.map((o) => [o.id, o]));
  const ordnerPfad = (id: string | null): string => {
    const teile: string[] = [];
    const gesehen = new Set<string>();
    while (id && ordnerNachId.has(id) && !gesehen.has(id)) {
      gesehen.add(id);
      const o = ordnerNachId.get(id)!;
      teile.unshift(sauber(o.name));
      id = o.parent_id;
    }
    return teile.join('/');
  };

  const { ZipSchreiber } = await import('../zip.ts');
  const datum = new Date().toISOString().slice(0, 10);
  const gesamt = state.opened.length;
  const fehler: string[] = [];
  const belegt = new Set<string>();
  let zip = new ZipSchreiber();
  let paket = 0;
  let fertig = 0;

  const abschliessen = (): void => {
    if (!zip.dateien) return;
    paket++;
    speichern(zip.fertig(), `Akten-Safe-Export-${datum}-Teil-${String(paket).padStart(2, '0')}.zip`);
    zip = new ZipSchreiber();
  };

  for (const dokument of state.opened) {
    fortschritt(`Dokument ${fertig + 1} von ${gesamt} …`);
    try {
      const daten = new Uint8Array(await (await downloadDocument(dokument)).arrayBuffer());
      if (zip.dateien && zip.groesse + daten.length > paketBytes) {
        abschliessen();
        // Browser brauchen einen Atemzug, um mehrere Downloads nacheinander zuzulassen.
        await new Promise((r) => window.setTimeout(r, 600));
      }
      const ordner = ordnerPfad(dokument.meta.folder_id);
      // Mehrere Safes: jeder bekommt im Export seinen eigenen Oberordner.
      const safe = alleSafes().length > 1 ? safeVon(dokument.meta.safe_kunde_id) : undefined;
      const schrank = safe ? `${sauber(safe.titel)}/` : '';
      let pfad = `${schrank}${ordner ? `${ordner}/` : ''}${sauber(dokument.filename)}`;
      for (let n = 2; belegt.has(pfad.toLowerCase()); n++) {
        const punkt = pfad.lastIndexOf('.');
        const hatEndung = punkt > pfad.lastIndexOf('/') + 1;
        pfad = hatEndung ? `${pfad.slice(0, punkt)} (${n})${pfad.slice(punkt)}` : `${pfad} (${n})`;
      }
      belegt.add(pfad.toLowerCase());
      zip.add(pfad, daten, new Date(dokument.meta.created_at));
    } catch (error) {
      fehler.push(`${dokument.filename}: ${fehlertext(error)}`);
    }
    fertig++;
  }
  abschliessen();
  return { dokumente: gesamt - fehler.length, pakete: paket, fehler };
}
